import React, { useEffect, useRef, useCallback } from "react";
import { ClerkProvider, useUser, useAuth } from "@clerk/react";
import "./App.css";
import LoginPage from "./components/LoginPage";
import AdminDashboard from "./components/AdminDashboard";
import UserDashboard from "./components/UserDashboard";
import AccountPage from "./components/AccountPage";
import AcceptInvitation from "./components/AcceptInvitation";

const API_URL         = process.env.REACT_APP_API_URL || "";
const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const INACTIVITY_MS = 2 * 60 * 60 * 1000; // 2 hours

function AppInner() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut, getToken }          = useAuth();
  const [showAccount, setShowAccount]  = React.useState(false);
  const loggedRef  = useRef(false);
  const timerRef   = useRef(null);

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!isSignedIn) return;
    timerRef.current = setTimeout(() => signOut(), INACTIVITY_MS);
  }, [isSignedIn, signOut]);

  useEffect(() => {
    if (!isSignedIn) return;
    resetTimer();
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  useEffect(() => {
    if (!isSignedIn || !user || loggedRef.current) return;
    loggedRef.current = true;
    const userId = user.primaryEmailAddress?.emailAddress || user.username || "";
    if (!userId) return;
    getToken().then(token => {
      // Ensure user exists in D1 (covers manual Clerk accounts + webhook delays)
      fetch(`${API_URL}/api/registerUser`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
      fetch(`${API_URL}/api/log-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, accessed_at: new Date().toISOString() }),
      }).catch(() => {});
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user]);

  if (window.location.pathname === "/accept-invitation") {
    if (!isLoaded) return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }} />
    );
    if (!isSignedIn) return <AcceptInvitation />;
    const hasTicket = new URLSearchParams(window.location.search).has("__clerk_ticket");
    if (hasTicket) {
      signOut({ redirectUrl: window.location.href });
      return <div style={{ minHeight: "100vh", background: "#fff" }} />;
    }
    window.location.href = "/";
    return null;
  }

  if (!isLoaded) {
    return <div style={{ minHeight: "100vh", background: "#fff" }} />;
  }

  if (!isSignedIn) {
    return <LoginPage />;
  }

  const role = user.publicMetadata?.role || "user";

  return (
    <>
      {showAccount ? (
        <AccountPage onBack={() => setShowAccount(false)} />
      ) : role === "admin" ? (
        <AdminDashboard logout={() => signOut()} onOpenAccount={() => setShowAccount(true)} />
      ) : (
        <UserDashboard logout={() => signOut()} onOpenAccount={() => setShowAccount(true)} />
      )}
    </>
  );
}

function App() {
  if (!PUBLISHABLE_KEY) {
    return (
      <div style={{ padding: 40, fontFamily: "Inter, sans-serif", color: "#dc2626" }}>
        <strong>Missing REACT_APP_CLERK_PUBLISHABLE_KEY</strong>
        <p>Add it to your <code>.env</code> file and restart.</p>
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      signInUrl="/"
      signUpUrl="/accept-invitation"
      afterSignInUrl="/"
      afterSignUpUrl="/"
    >
      <AppInner />
    </ClerkProvider>
  );
}

export default App;
